import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import * as Sentry from "@sentry/react";
import { getCurrentUser } from "@/services/auth";

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  sendToken?: boolean;
}

const axiosClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

axiosClient.interceptors.request.use(
  async (config: CustomAxiosRequestConfig) => {
    return Sentry.startSpanManual(
      { name: "axios_interceptor", op: "http.client" },
      async (span) => {
        if (config.sendToken) {
          delete config.sendToken;
          const user = await getCurrentUser();
          if (user) {
            const token = await user.getIdToken();
            if (token) {
              config.headers = config.headers || {};
              config.headers.Authorization = `Bearer ${token}`;
            }
          }
        }
        span.setAttributes({
          "http.method": config.method?.toUpperCase(),
          "http.url": config.url,
          "http.request_body": JSON.stringify(config.data),
        });
        config.sentrySpan = span;
        return config;
      }
    );
  },
  (error) => {
    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
    return Promise.reject(error);
  }
);

axiosClient.interceptors.response.use(
  (response) => {
    const span = response.config.sentrySpan;
    if (span) {
      span.setStatus({ code: 1 });
      span.setAttribute("status_code", response.status);
      span.end();
    }
    return response;
  },
  (error: AxiosError) => {
    const span = error.config?.sentrySpan as Partial<Sentry.Span>;
    span.setStatus?.({ code: 2 });
    if (error.response) {
      span.setAttribute?.("status_code", error.status);
    }
    span.end?.();
    return Promise.reject(error);
  }
);

export default axiosClient;
